import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { haafPaymentKeys } from "./keys";
import {
  createHaafPayment,
  deleteHaafPayment,
  getAllHaafPayments,
  updateHaafPayment,
  verifyHaafPayment,
  verifyAllHaafPayments,
  unverifyHaafPayment,
  getChapterHaafPayments,
  getHaafPaymentsByBatch,
  getHaafPaymentStats,
  getHaafPaymentBatchStats,
  getHaafPaymentChapterStats,
  getHaafStatisticsAlumniCoverage,
  getHaafStatisticsBreakdown,
  getHaafStatisticsComparison,
  getHaafStatisticsLeaderboard,
  getHaafStatisticsOverview,
  getHaafStatisticsPending,
  getHaafStatisticsRecentActivity,
  getHaafStatisticsTimeseries,
  getHaafMeta,
  getHaafFilterOptions,
  getHaafFinancialYears,
  createHaafFinancialYear,
  searchHaafAlumni,
} from "./api";
import { toast } from "sonner";

export const useHaafPayments = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.list(financialYearId),
    queryFn: () => getAllHaafPayments(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafMeta = () => {
  return useQuery({
    queryKey: haafPaymentKeys.meta(),
    queryFn: getHaafMeta,
  });
};

export const useHaafFilterOptions = () => {
  return useQuery({
    queryKey: haafPaymentKeys.filterOptions(),
    queryFn: getHaafFilterOptions,
    staleTime: 30 * 60 * 1000,
  });
};

export const useHaafFinancialYears = () => {
  return useQuery({
    queryKey: haafPaymentKeys.financialYears(),
    queryFn: getHaafFinancialYears,
  });
};

export const useCreateHaafFinancialYear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createHaafFinancialYear,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: haafPaymentKeys.financialYears(),
      });
      toast.success("HAF financial year created successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create HAF financial year"
      );
    },
  });
};

export const useHaafAlumniSearch = (search) => {
  return useQuery({
    queryKey: haafPaymentKeys.alumniSearch(search),
    queryFn: () => searchHaafAlumni(search),
    enabled: !!search && search.trim().length >= 2,
  });
};

export const useCreateHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createHaafPayment,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.list() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.statistics() });
      toast.success(
        variables?.isVerified
          ? "HAAF Payment created and verified successfully"
          : "HAAF Payment created successfully",
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create HAAF payment"
      );
    },
  });
};

export const useUpdateHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.statistics() });
      toast.success("HAAF Payment updated successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update HAAF payment"
      );
    },
  });
};

export const useDeleteHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.statistics() });
      toast.success("HAAF Payment deleted successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to delete HAAF payment"
      );
    },
  });
};

export const useVerifyHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.statistics() });
      toast.success("HAAF Payment verified successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to verify HAAF payment"
      );
    },
  });
};

export const useVerifyAllHaafPayments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyAllHaafPayments,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.statistics() });
      toast.success(
        data?.message ||
          `Verified ${data?.modifiedCount ?? "all pending"} HAAF payments`,
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to verify all HAAF payments",
      );
    },
  });
};

export const useUnverifyHaafPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unverifyHaafPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: haafPaymentKeys.statistics() });
      toast.success("HAAF Payment unverified successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to unverify HAAF payment"
      );
    },
  });
};

export const useChapterHaafPayments = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.chapterList(financialYearId),
    queryFn: () => getChapterHaafPayments(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafPaymentsByBatch = (batch, financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.batchList(batch, financialYearId),
    queryFn: () => getHaafPaymentsByBatch(batch, financialYearId),
    enabled: !!batch && !!financialYearId,
  });
};

export const useHaafPaymentStats = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.stats(financialYearId),
    queryFn: () => getHaafPaymentStats(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafPaymentBatchStats = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.batchStats(financialYearId),
    queryFn: () => getHaafPaymentBatchStats(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafPaymentChapterStats = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.chapterStats(financialYearId),
    queryFn: () => getHaafPaymentChapterStats(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafStatisticsOverview = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsOverview(financialYearId),
    queryFn: () => getHaafStatisticsOverview(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafStatisticsTimeseries = (
  financialYearId,
  interval = "day",
) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsTimeseries(financialYearId, interval),
    queryFn: () => getHaafStatisticsTimeseries(financialYearId, interval),
    enabled: !!financialYearId,
  });
};

export const useHaafStatisticsBreakdown = (financialYearId, groupBy) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsBreakdown(financialYearId, groupBy),
    queryFn: () => getHaafStatisticsBreakdown(financialYearId, groupBy),
    enabled: !!financialYearId && !!groupBy,
  });
};

export const useHaafStatisticsLeaderboard = (
  financialYearId,
  groupBy,
  limit = 10,
) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsLeaderboard(
      financialYearId,
      groupBy,
      limit,
    ),
    queryFn: () =>
      getHaafStatisticsLeaderboard(financialYearId, groupBy, limit),
    enabled: !!financialYearId && !!groupBy,
  });
};

export const useHaafStatisticsPending = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsPending(financialYearId),
    queryFn: () => getHaafStatisticsPending(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafStatisticsRecentActivity = (
  financialYearId,
  limit = 10,
) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsRecentActivity(financialYearId, limit),
    queryFn: () => getHaafStatisticsRecentActivity(financialYearId, limit),
    enabled: !!financialYearId,
  });
};

export const useHaafStatisticsAlumniCoverage = (financialYearId) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsAlumniCoverage(financialYearId),
    queryFn: () => getHaafStatisticsAlumniCoverage(financialYearId),
    enabled: !!financialYearId,
  });
};

export const useHaafStatisticsComparison = (
  financialYearId,
  compareWith = "previousFY",
) => {
  return useQuery({
    queryKey: haafPaymentKeys.statisticsComparison(
      financialYearId,
      compareWith,
    ),
    queryFn: () => getHaafStatisticsComparison(financialYearId, compareWith),
    enabled: !!financialYearId,
  });
};
